import { Check, ExternalLink, MapPin, Plus, Search, Store, X, Zap } from "lucide-react";
import { useState, useEffect, useRef, useCallback } from "react";
import BONavigation from "../../../components/admin/BONavigation";
import {
    getUnfranchisedEstablishments,
    bulkUpdateEstablishmentFranchise,
    getFranchises,
    createFranchise,
    FranchiseDoc,
} from "../../../interfaces/establishments";
import { IEstablishment } from "../../../interfaces/establishment";

function FranchisePicker({
    franchises,
    selected,
    onSelect,
    onCreated,
}: {
    franchises: FranchiseDoc[];
    selected: FranchiseDoc | null;
    onSelect: (f: FranchiseDoc | null) => void;
    onCreated: (f: FranchiseDoc) => void;
}) {
    const [query, setQuery] = useState("");
    const [open, setOpen] = useState(false);
    const [creating, setCreating] = useState(false);
    const [createError, setCreateError] = useState<string | null>(null);
    const inputRef = useRef<HTMLInputElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);

    const filtered = query.length > 0
        ? franchises.filter(f => f.name.toLowerCase().includes(query.toLowerCase())).slice(0, 10)
        : franchises.slice(0, 10);

    const exactMatch = franchises.some(f => f.name.toLowerCase() === query.trim().toLowerCase());

    const handleSelect = (f: FranchiseDoc) => {
        onSelect(f);
        setQuery("");
        setOpen(false);
    };

    const handleCreate = async () => {
        const name = query.trim();
        if (!name) return;
        setCreating(true);
        setCreateError(null);
        try {
            const f = await createFranchise(name);
            onCreated(f);
            onSelect(f);
            setQuery("");
            setOpen(false);
        } catch (err: any) {
            setCreateError(err?.response?.data?.error || "Erreur lors de la création");
        } finally {
            setCreating(false);
        }
    };

    useEffect(() => {
        const handler = (e: MouseEvent) => {
            if (containerRef.current && !containerRef.current.contains(e.target as Node)) setOpen(false);
        };
        document.addEventListener("mousedown", handler);
        return () => document.removeEventListener("mousedown", handler);
    }, []);

    return (
        <div ref={containerRef} className="relative">
            <p className="text-[11px] font-semibold text-gray-500 uppercase tracking-wide mb-1.5 flex items-center gap-1.5">
                <span className="w-1.5 h-1.5 rounded-full bg-orange-500" />
                Franchise
            </p>

            {selected ? (
                <div className="flex items-center gap-2 border border-orange-200 rounded-sm px-3 py-2 bg-orange-50">
                    <Store className="w-3.5 h-3.5 text-orange-500 shrink-0" />
                    <span className="text-xs font-medium text-orange-800 flex-1 truncate">{selected.name}</span>
                    <button type="button" onClick={() => onSelect(null)} className="hover:opacity-70 transition-opacity">
                        <X className="w-3.5 h-3.5 text-orange-600" />
                    </button>
                </div>
            ) : (
                <div
                    className="flex items-center gap-2 border border-gray-200 rounded-sm px-3 py-2 cursor-text bg-white focus-within:border-gray-400 focus-within:ring-1 focus-within:ring-gray-200 transition-all"
                    onClick={() => { inputRef.current?.focus(); setOpen(true); }}
                >
                    <Search className="w-3.5 h-3.5 text-gray-400 shrink-0" />
                    <input
                        ref={inputRef}
                        type="text"
                        value={query}
                        onChange={e => { setQuery(e.target.value); setOpen(true); setCreateError(null); }}
                        onFocus={() => setOpen(true)}
                        placeholder="Rechercher une franchise..."
                        className="flex-1 border-0 outline-none focus:ring-0 text-xs text-gray-700 placeholder:text-gray-400 bg-transparent"
                    />
                    {query && (
                        <button type="button" onClick={() => { setQuery(""); setCreateError(null); }} className="text-gray-400 hover:text-gray-600">
                            <X className="w-3 h-3" />
                        </button>
                    )}
                </div>
            )}

            {open && !selected && (
                <div className="absolute z-30 mt-1 w-full bg-white border border-gray-200 rounded-sm shadow-sm max-h-56 overflow-y-auto">
                    {filtered.map(f => (
                        <button
                            key={f._id}
                            type="button"
                            onMouseDown={e => { e.preventDefault(); handleSelect(f); }}
                            className="w-full text-left px-3 py-2 text-xs text-gray-700 hover:bg-[#F9F6F3] flex items-center gap-2"
                        >
                            <Store className="w-3 h-3 text-gray-400 shrink-0" />
                            {f.name}
                        </button>
                    ))}
                    {query.trim() && !exactMatch && (
                        <button
                            type="button"
                            disabled={creating}
                            onMouseDown={e => { e.preventDefault(); handleCreate(); }}
                            className="w-full text-left px-3 py-2 text-xs text-gray-700 hover:bg-[#F9F6F3] flex items-center gap-2 border-t border-gray-100"
                        >
                            <Plus className="w-3 h-3 text-green-500 shrink-0" />
                            {creating ? "Création..." : <>Créer <strong>"{query.trim()}"</strong></>}
                        </button>
                    )}
                    {filtered.length === 0 && !query.trim() && (
                        <p className="px-3 py-3 text-xs text-gray-400">Aucune franchise</p>
                    )}
                </div>
            )}

            {createError && (
                <p className="mt-1 text-[11px] text-red-500">{createError}</p>
            )}
        </div>
    );
}

function formatDate(value?: string) {
    if (!value) return "—";
    const d = new Date(value);
    return isNaN(d.getTime()) ? "—" : d.toLocaleDateString("fr-FR", { day: "2-digit", month: "short", year: "numeric" });
}

function ExistingFranchiseBadge({ franchise }: { franchise?: { _id: string; name: string } | null }) {
    if (!franchise) return <span className="text-[10px] text-gray-300 italic">—</span>;
    return (
        <span className="px-1.5 py-0.5 rounded-sm text-[10px] font-medium border bg-orange-50 text-orange-600 border-orange-100">
            {franchise.name}
        </span>
    );
}

export default function TodoFranchise() {
    const [query, setQuery] = useState("");
    const [suggestOpen, setSuggestOpen] = useState(false);
    const searchRef = useRef<HTMLDivElement>(null);
    const [page, setPage] = useState(1);
    const [results, setResults] = useState<IEstablishment[]>([]);
    const [total, setTotal] = useState(0);
    const [totalUnfranchised, setTotalUnfranchised] = useState<number | null>(null);
    const [totalPages, setTotalPages] = useState(0);
    const [loading, setLoading] = useState(false);
    const [hasSearched, setHasSearched] = useState(false);

    const [selected, setSelected] = useState<Set<string>>(new Set());

    const [franchises, setFranchises] = useState<FranchiseDoc[]>([]);
    const [pickedFranchise, setPickedFranchise] = useState<FranchiseDoc | null>(null);

    const [applying, setApplying] = useState(false);
    const [applyResult, setApplyResult] = useState<number | null>(null);

    const LIMIT = 25;

    const fetchResults = useCallback(async (q: string, pg: number) => {
        setLoading(true);
        setHasSearched(true);
        try {
            const data = await getUnfranchisedEstablishments({ q, page: pg, limit: LIMIT });
            setResults(data.establishments);
            setTotal(data.total);
            setTotalUnfranchised(data.totalUnfranchised);
            setTotalPages(data.totalPages);
        } catch (err) {
            console.error("Search failed", err);
        } finally {
            setLoading(false);
        }
    }, []);

    const fetchFranchises = useCallback(async () => {
        try {
            const data = await getFranchises();
            setFranchises(data);
        } catch (err) {
            console.error("Failed to fetch franchises", err);
        }
    }, []);

    // Debounced search
    useEffect(() => {
        const timer = setTimeout(() => {
            setPage(1);
            setSelected(new Set());
            fetchResults(query, 1);
        }, 350);
        return () => clearTimeout(timer);
    }, [query, fetchResults]);

    useEffect(() => {
        fetchFranchises();
    }, [fetchFranchises]);

    useEffect(() => {
        const handler = (e: MouseEvent) => {
            if (searchRef.current && !searchRef.current.contains(e.target as Node)) setSuggestOpen(false);
        };
        document.addEventListener("mousedown", handler);
        return () => document.removeEventListener("mousedown", handler);
    }, []);

    const trimmedQuery = query.trim().toLowerCase();
    const suggestions = trimmedQuery.length > 0
        ? franchises
            .filter(f => f.name.toLowerCase().includes(trimmedQuery) && f.name.toLowerCase() !== trimmedQuery)
            .sort((a, b) => Number(!a.name.toLowerCase().startsWith(trimmedQuery)) - Number(!b.name.toLowerCase().startsWith(trimmedQuery)))
            .slice(0, 8)
        : [];

    // Picking a franchise searches its name and preselects it in the apply panel
    const pickSuggestion = (f: FranchiseDoc) => {
        setQuery(f.name);
        setPickedFranchise(f);
        setSuggestOpen(false);
    };

    const allPageIds = results.map(r => r._id);
    const allPageSelected = allPageIds.length > 0 && allPageIds.every(id => selected.has(id));

    const toggleSelectAll = () => {
        if (allPageSelected) {
            setSelected(prev => {
                const next = new Set(prev);
                allPageIds.forEach(id => next.delete(id));
                return next;
            });
        } else {
            setSelected(prev => new Set([...prev, ...allPageIds]));
        }
    };

    const toggleOne = (id: string) => {
        setSelected(prev => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    };

    const handlePageChange = (pg: number) => {
        setPage(pg);
        fetchResults(query, pg);
        window.scrollTo({ top: 0, behavior: "smooth" });
    };

    const handleApply = async () => {
        if (selected.size === 0 || !pickedFranchise) return;
        setApplying(true);
        setApplyResult(null);
        try {
            const result = await bulkUpdateEstablishmentFranchise([...selected], pickedFranchise._id);
            setApplyResult(result.modifiedCount);
            setSelected(new Set());
            fetchResults(query, page);
        } catch (err) {
            console.error("Bulk apply failed", err);
        } finally {
            setApplying(false);
        }
    };

    const canApply = selected.size > 0 && pickedFranchise !== null;

    return (
        <div className="min-h-screen bg-[#F5F0EB]">
            <div className="mx-auto max-w-7xl px-4">
                {/* Header */}
                <div className="mb-6">
                    <h1 className="text-xl font-semibold text-black">Bulk Franchise</h1>
                    <p className="text-xs text-gray-600 mt-0.5">
                        Recherchez des établissements et associez-leur une franchise en masse
                    </p>
                </div>

                <BONavigation />

                <div className="flex gap-6">
                    {/* Left: Search + Results */}
                    <div className="flex-1 min-w-0">
                        {/* Search bar + filter */}
                        <div className="flex items-center gap-2 mb-4">
                            <div ref={searchRef} className="relative flex-1">
                                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                                <input
                                    type="text"
                                    value={query}
                                    onChange={e => { setQuery(e.target.value); setSuggestOpen(true); }}
                                    onFocus={() => setSuggestOpen(true)}
                                    onKeyDown={e => { if (e.key === "Escape") setSuggestOpen(false); }}
                                    placeholder="Rechercher par nom d'établissement ou de franchise..."
                                    className="w-full pl-9 pr-4 py-2.5 border border-gray-200 rounded-sm text-sm text-gray-900 bg-white placeholder:text-gray-400 focus:outline-none focus:border-gray-400 focus:ring-1 focus:ring-gray-200"
                                />
                                {query && (
                                    <button onClick={() => setQuery("")} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                                        <X className="w-4 h-4" />
                                    </button>
                                )}
                                {suggestOpen && suggestions.length > 0 && (
                                    <div className="absolute z-30 mt-1 w-full bg-white border border-gray-200 rounded-sm shadow-sm max-h-64 overflow-y-auto">
                                        <p className="px-3 pt-2 pb-1 text-[10px] font-semibold text-gray-400 uppercase tracking-wide">Franchises existantes</p>
                                        {suggestions.map(f => (
                                            <button
                                                key={f._id}
                                                type="button"
                                                onMouseDown={e => { e.preventDefault(); pickSuggestion(f); }}
                                                className="w-full text-left px-3 py-2 text-xs text-gray-700 hover:bg-[#F9F6F3] flex items-center gap-2"
                                            >
                                                <Store className="w-3 h-3 text-gray-400 shrink-0" />
                                                {f.name}
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>
                            {totalUnfranchised !== null && (
                                <span className="text-xs text-gray-500 shrink-0">
                                    {totalUnfranchised.toLocaleString("fr-FR")} sans franchise
                                </span>
                            )}
                        </div>

                        {/* Results table */}
                        <div className="border border-gray-200 rounded-sm overflow-hidden">
                            {/* Table header */}
                            <div className="flex items-center gap-3 px-3 py-2 bg-gray-50 border-b border-gray-200">
                                <input
                                    type="checkbox"
                                    checked={allPageSelected}
                                    onChange={toggleSelectAll}
                                    className="w-3.5 h-3.5 rounded border-gray-300 accent-black cursor-pointer"
                                />
                                <span className="text-[11px] font-semibold text-gray-500 uppercase tracking-wide flex-1">
                                    {loading ? "Chargement..." : hasSearched ? `${total.toLocaleString("fr-FR")} établissement${total > 1 ? "s" : ""}` : ""}
                                </span>
                                {selected.size > 0 && (
                                    <span className="text-[11px] font-semibold text-black bg-black/5 px-2 py-0.5 rounded-sm">
                                        {selected.size} sélectionné{selected.size > 1 ? "s" : ""}
                                    </span>
                                )}
                                <span className="text-[11px] text-gray-400 hidden sm:block w-28 text-center">Ajouté le</span>
                                <span className="text-[11px] text-gray-400 hidden sm:block w-32 text-center">Franchise</span>
                            </div>

                            {loading ? (
                                <div className="divide-y divide-gray-100">
                                    {Array.from({ length: 8 }).map((_, i) => (
                                        <div key={i} className="flex items-center gap-3 px-3 py-3 animate-pulse">
                                            <div className="w-3.5 h-3.5 bg-gray-100 rounded shrink-0" />
                                            <div className="w-8 h-8 bg-gray-100 rounded-sm shrink-0" />
                                            <div className="flex-1 space-y-1.5">
                                                <div className="h-3.5 bg-gray-100 rounded w-2/3" />
                                                <div className="h-2.5 bg-gray-100 rounded w-1/3" />
                                            </div>
                                            <div className="w-32 h-5 bg-gray-100 rounded hidden sm:block" />
                                        </div>
                                    ))}
                                </div>
                            ) : results.length === 0 ? (
                                <div className="py-16 text-center">
                                    <Store className="w-8 h-8 text-gray-200 mx-auto mb-2" />
                                    <p className="text-sm text-gray-400">
                                        {hasSearched && query ? `Aucun résultat pour "${query}"` : "Aucun établissement"}
                                    </p>
                                </div>
                            ) : (
                                <div className="divide-y divide-gray-100">
                                    {results.map(est => {
                                        const city = est.address?.city;
                                        const postcode = est.address?.postcode;
                                        const location = [postcode, city].filter(Boolean).join(" ");
                                        const isSelected = selected.has(est._id);
                                        const mapsUrl = est.ratings?.source_summary?.find((s: any) => s.source === "google")?.url ?? null;
                                        const siren = est.registration?.siren;
                                        const pappersUrl = siren ? `https://www.pappers.fr/entreprise/${siren}` : null;

                                        return (
                                            <div
                                                key={est._id}
                                                onClick={() => toggleOne(est._id)}
                                                className={`flex items-center gap-3 px-3 py-2.5 cursor-pointer transition-colors ${isSelected ? "bg-black/[0.03]" : "hover:bg-[#F9F6F3]/60"}`}
                                            >
                                                <input
                                                    type="checkbox"
                                                    checked={isSelected}
                                                    onChange={() => toggleOne(est._id)}
                                                    onClick={e => e.stopPropagation()}
                                                    className="w-3.5 h-3.5 rounded border-gray-300 accent-black cursor-pointer shrink-0"
                                                />
                                                <div className="w-8 h-8 rounded-sm bg-gray-100 flex items-center justify-center shrink-0">
                                                    <Store className="w-4 h-4 text-gray-400" />
                                                </div>
                                                <div className="flex-1 min-w-0">
                                                    <p className="text-xs font-semibold text-gray-900 truncate">{est.name}</p>
                                                    <div className="flex items-center gap-1.5 mt-0.5">
                                                        {location && (
                                                            <span className="text-[10px] text-gray-500 flex items-center gap-0.5">
                                                                <MapPin className="w-2.5 h-2.5" />{location}
                                                            </span>
                                                        )}
                                                        {est.type && (
                                                            <span className="px-1 py-0.5 rounded-sm bg-gray-100 text-[10px] text-gray-500 border border-gray-200">{est.type}</span>
                                                        )}
                                                        {est.brand_group && (
                                                            <span className="px-1 py-0.5 rounded-sm bg-gray-100 text-[10px] text-gray-700 border border-gray-200">{est.brand_group}</span>
                                                        )}
                                                    </div>
                                                </div>
                                                <div className="hidden sm:block w-28 shrink-0 text-center">
                                                    <p className="text-[11px] text-gray-700">{formatDate(est.createdAt)}</p>
                                                    {est.operations?.opening_date && (
                                                        <p className="text-[10px] text-gray-400">Ouvert le {formatDate(est.operations.opening_date)}</p>
                                                    )}
                                                </div>
                                                <div className="hidden sm:block w-32 shrink-0">
                                                    <ExistingFranchiseBadge franchise={est.franchise} />
                                                </div>
                                                <div className="flex items-center gap-1 shrink-0" onClick={e => e.stopPropagation()}>
                                                    {mapsUrl && (
                                                        <a href={mapsUrl} target="_blank" rel="noopener noreferrer"
                                                            title="Google Maps"
                                                            className="p-1 rounded text-gray-300 hover:text-red-500 hover:bg-red-50 transition-colors">
                                                            <MapPin className="w-3.5 h-3.5" />
                                                        </a>
                                                    )}
                                                    {pappersUrl && (
                                                        <a href={pappersUrl} target="_blank" rel="noopener noreferrer"
                                                            title="Pappers"
                                                            className="p-1 rounded text-gray-300 hover:text-gray-700 hover:bg-[#333] transition-colors">
                                                            <ExternalLink className="w-3.5 h-3.5" />
                                                        </a>
                                                    )}
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </div>

                        {/* Pagination */}
                        {totalPages > 1 && (
                            <div className="flex items-center justify-center gap-2 mt-4">
                                <button
                                    onClick={() => handlePageChange(page - 1)}
                                    disabled={page <= 1}
                                    className="px-3 py-1.5 border border-gray-200 rounded-sm text-xs text-gray-600 hover:border-gray-300 disabled:opacity-40 disabled:cursor-not-allowed"
                                >
                                    Précédent
                                </button>
                                <span className="text-xs text-gray-500">Page {page} / {totalPages}</span>
                                <button
                                    onClick={() => handlePageChange(page + 1)}
                                    disabled={page >= totalPages}
                                    className="px-3 py-1.5 border border-gray-200 rounded-sm text-xs text-gray-600 hover:border-gray-300 disabled:opacity-40 disabled:cursor-not-allowed"
                                >
                                    Suivant
                                </button>
                            </div>
                        )}
                    </div>

                    {/* Right: Apply panel */}
                    <div className="w-72 shrink-0">
                        <div className="sticky top-4 border border-gray-200 rounded-sm p-4 bg-white">
                            <div className="flex items-center gap-2 mb-4">
                                <Zap className="w-4 h-4 text-gray-500" />
                                <span className="text-sm font-semibold text-gray-900">Assigner une franchise</span>
                            </div>

                            {/* Selection summary */}
                            <div className={`mb-4 p-2.5 rounded-sm text-xs ${selected.size > 0 ? "bg-black text-white" : "bg-gray-50 text-gray-500 border border-gray-200"}`}>
                                {selected.size > 0 ? (
                                    <span className="font-semibold">{selected.size} établissement{selected.size > 1 ? "s" : ""} sélectionné{selected.size > 1 ? "s" : ""}</span>
                                ) : (
                                    "Sélectionnez des établissements dans la liste"
                                )}
                            </div>

                            {/* Franchise picker */}
                            <div className="mb-4">
                                <FranchisePicker
                                    franchises={franchises}
                                    selected={pickedFranchise}
                                    onSelect={setPickedFranchise}
                                    onCreated={f => setFranchises(prev => [...prev, f].sort((a, b) => a.name.localeCompare(b.name)))}
                                />
                            </div>

                            {/* Warning */}
                            {pickedFranchise && (
                                <p className="text-[10px] text-gray-400 mb-3">
                                    La franchise existante sera remplacée sur les établissements sélectionnés.
                                </p>
                            )}

                            {/* Apply button */}
                            <button
                                onClick={handleApply}
                                disabled={!canApply || applying}
                                className={`w-full flex items-center justify-center gap-2 px-4 py-2 rounded-sm text-xs font-semibold transition-all ${
                                    canApply
                                        ? "bg-black text-white hover:bg-gray-800"
                                        : "bg-gray-100 text-gray-400 cursor-not-allowed"
                                }`}
                            >
                                {applying ? (
                                    <>
                                        <span className="w-3.5 h-3.5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                                        Application...
                                    </>
                                ) : (
                                    <>
                                        <Zap className="w-3.5 h-3.5" />
                                        {selected.size > 0
                                            ? `Appliquer à ${selected.size} établissement${selected.size > 1 ? "s" : ""}`
                                            : "Appliquer la franchise"
                                        }
                                    </>
                                )}
                            </button>

                            {/* Success feedback */}
                            {applyResult !== null && (
                                <div className="mt-3 flex items-center gap-2 p-2.5 bg-green-50 border border-green-200 rounded-sm">
                                    <Check className="w-3.5 h-3.5 text-green-600 shrink-0" />
                                    <p className="text-xs text-green-700 font-medium">
                                        {applyResult} établissement{applyResult > 1 ? "s" : ""} mis à jour
                                    </p>
                                    <button onClick={() => setApplyResult(null)} className="ml-auto text-green-500 hover:text-green-700">
                                        <X className="w-3 h-3" />
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
